import { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import CloseIconSvg from '../../assets/icons/closeIcon.svg';
import OpenInNewIconSvg from '../../assets/icons/openInNew.svg';
import CustomBackdrop from './CustomBackdrop';
import useCommonMediaQuery from '../../hooks/useCommonMediaQuery';

const AnnouncementDialog = memo(
  ({
    open,
    announcement,
    onClose,
    onDownloadAttachment,
    isAdmin = false,
    formatDate,
  }) => {
    const isSmallScreen = useCommonMediaQuery('sm');
    if (!announcement) return null;
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    return (
      <>
        <CustomBackdrop
          open={open}
          modalDialogSelector=".announcement-dialog-dialog, .MuiDialog-root"
          blurIntensity={1}
        />
        <Dialog
          className="announcement-dialog-dialog"
          open={open}
          onClose={(e, reason) => {
            if (reason !== 'backdropClick') onClose();
          }}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallScreen}
          disableScrollLock={true}
          onKeyDown={handleKeyDown}
          aria-labelledby="announcement-dialog-title"
          aria-describedby="announcement-dialog-description"
          sx={{ zIndex: 10002 }}
          PaperProps={{
            className: 'announcement-dialog-dialog',
            sx: {
              zIndex: 10002,
              display: 'flex',
              flexDirection: 'column',
              maxHeight: isSmallScreen ? '100%' : 'calc(100vh - 32px)',
            },
          }}
        >
          <DialogTitle
            id="announcement-dialog-title"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1,
              backgroundColor: 'transparent',
              borderBottom: '1px solid #e5e7eb',
              p: { xs: 1.5, sm: 1 },
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#1f2937',
                }}
              >
                Announcement Details
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="Close dialog"
              sx={{
                color: '#6b7280',
                borderRadius: '50%',
              }}
            >
              <img src={CloseIconSvg} alt="Close" width={35} height={35} />
            </IconButton>
          </DialogTitle>
          <DialogContent
            id="announcement-dialog-description"
            sx={{
              px: { xs: '16px !important', sm: '24px !important' },
              py: { xs: '16px !important', sm: '24px !important' },
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              minHeight: 0,
              backgroundColor: '#F4F6F6',
            }}
          >
            {/* Main Content Card */}
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                backgroundColor: '#ffffff',
                borderRadius: 2,
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
              }}
            >
              {/* Title and Actions Row */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1f2937',
                    flex: 1,
                    mb: { xs: 0, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  {announcement.title}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    alignSelf: { xs: 'flex-start', sm: 'auto' },
                  }}
                >
                  {announcement.mediaUrl &&
                    announcement.mediaUrl.trim() !== '' && (
                      <Typography
                        variant="body2"
                        onClick={() =>
                          onDownloadAttachment(announcement.mediaUrl)
                        }
                        sx={{
                          color: '#3b82f6',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          '&:hover': {
                            color: '#f1ac2f',
                            '& img': {
                              filter:
                                'brightness(0) saturate(100%) invert(71%) sepia(95%) saturate(1352%) hue-rotate(349deg) brightness(95%) contrast(89%)',
                            },
                          },
                        }}
                      >
                        Attachment
                        <img src={OpenInNewIconSvg} alt="Open in New" />
                      </Typography>
                    )}
                </Box>
              </Box>

              {/* Content Body */}
              <Typography
                variant="body1"
                sx={{
                  color: '#1f2937',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  mb: 2,
                  minHeight: '100px',
                }}
              >
                {announcement.content}
              </Typography>
              {isAdmin && (
                <>
                  {/* Separator */}
                  <Divider sx={{ my: 2 }} />

                  {/* Date Information Row */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { sm: 1, md: 2, lg: 3 },
                      flexWrap: 'wrap',
                    }}
                  >
                    {announcement.startDate && (
                      <Typography variant="body2" sx={{ color: '#1f2937' }}>
                        Start Date: {formatDate(announcement.startDate)}
                      </Typography>
                    )}
                    {announcement.endDate && (
                      <Typography variant="body2" sx={{ color: '#1f2937' }}>
                        End Date: {formatDate(announcement.endDate)}
                      </Typography>
                    )}

                    {announcement.createdAt && (
                      <Typography variant="body2" sx={{ color: '#1f2937' }}>
                        Created Date: {formatDate(announcement.createdAt)}
                      </Typography>
                    )}
                    {announcement.updatedAt && (
                      <Typography variant="body2" sx={{ color: '#1f2937' }}>
                        Updated Date: {formatDate(announcement.updatedAt)}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Paper>
          </DialogContent>
          <DialogActions
            sx={{
              p: { xs: 1.5, sm: 1 },
              borderTop: '1px solid #e5e7eb',
              justifyContent: 'flex-end',
              flexShrink: 0,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Button
              onClick={onClose}
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#486791',
                padding: '4px 12px !important',
                color: '#ffffff',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#3d5a7a',
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default AnnouncementDialog;
